import { useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Mail, Printer } from "lucide-react";

const SPECS = `Decline Drive™ — Complete Product Specifications
Ayers Enterprises

ACCESS
- App URL: https://repair-remind-1.preview.emergentagent.com
- Owner login: jayers.me@gmail.com / Denali2020$
- Contact: James Ayers — jayers.me@gmail.com

WHAT IT DOES
When a customer declines recommended service, Decline Drive automatically sends a discounted offer text on Day {stage_1_days} and a deeper offer on Day {stage_2_days}. The exact discount % is pulled live from the dealer's Settings. Each text contains a unique coupon code, a tap-to-schedule link, and a tap-to-call fallback. The dashboard tracks every step from decline → text → schedule → RO closed → revenue recovered.

FEATURES (LIVE NOW)
Customer-facing:
- Public schedule page with dealership branding and dashed-gold unique coupon code (e.g. DD-30-A7B2K9)
- Inline calendar with time-slot picker
- Confirmation card with post-discount total
- Click-to-call landing page
- No customer login or account creation required

Dashboard (staff):
- 6 KPI cards: Declined · Stage 1 Sent · Stage 2 Sent · Live Person · Scheduled · Revenue Recovered
- Per-advisor scorecard with conversion %
- Declined Services pipeline with advisor + status filters
- Nightly Import (accepts any DMS CSV: CDK, Reynolds, Dealertrack, Tekion)
- Run Campaign Cycle
- Send Test SMS to any phone number
- Boost Demo (hidden when Demo Mode = OFF)
- Take the Tour (guided walkthrough)
- Print button on every list

Automation:
- Nightly cron drains in the dealer's quiet-hours window, fires all due Stage 1 + Stage 2 texts
- Idempotent, deterministic coupon codes per decline

Billing:
- Monthly Statement with configurable pricing
- One-time onboarding: $895
- Monthly subscription: $1,599 per rooftop
- Contract: 12 months, auto-renew
- Print-ready statement (Recurring + Onboarding + Total Due)
- Pay by check + ACH — both configurable
- Mark-onboarding-billed so it disappears after month 1

Marketing collateral (built into the app):
- Dealer Flyer — printable one-page sell sheet with dynamic ROI math
- Roadmap — printable Q1–Q4 2026 feature plan
- Service Agreement (Contract) — printable 9-section contract, auto-fills dealership + pricing + remit info, 30-day onboarding refund clause, TCPA clause, dual signature blocks

Settings:
- Dealership name + live-person phone
- Stage 1/2 days + discount %
- Fully editable SMS templates with live preview + 9 placeholder chips
- Pricing controls
- Remittance info (check + ACH)
- Owner name/phone/email
- Demo Mode toggle

Advisors:
- Add / edit / delete
- Auto-creation from nightly CSV imports

SMS log:
- Every mock or live SMS with full body, coupon code, timestamp, stage, provider

Extra Campaigns (Live):
- Free Oil Change blast — pick a customer list, fire a FREE oil change offer with unique coupon
- Parts Arrived notification — upload a CSV, every row auto-texts "Your parts arrived, call {advisor}"
- CSI (Customer Satisfaction) follow-up — auto-fires 24 hours after RO close, routes unhappy customers to Service Manager

Appointments:
- List view + Calendar view (gold dots on booked days)
- Close appointment + enter RO total → feeds Statement page

DESIGN
- Navy (#0f2b6b) + gold (#C9A227) palette
- Manrope + IBM Plex Sans fonts
- Decline Drive™ + "Ayers Enterprises" on every screen
- Top copyright strip + bottom footer + favicon

DATA FLOW — How Decline Drive gets customer data (3 tiers)

Tier 1 — Direct cloud DMS API (40% of prospects)
- Modern cloud-native DMS: Tekion, Dominion VUE, PBS, DealerSocket, Dealertrack, Xtime, DealerBuilt
- Dealer signs a 5-minute API consent form, data flows nightly forever
- Zero ongoing dealer effort

Tier 2 — DMS aggregator feed (50% of prospects — RECOMMENDED)
- DealerVault (Authenticom), Fortellis (CDK), IntegraLink, CDX
- Pulls from CDK, Reynolds, Auto/Mate, Dealertrack — every legacy DMS in the US
- CSV delivered nightly to Decline Drive SFTP box, auto-ingested overnight
- Dealer effort: sign one data consent form during onboarding
- Cost: ~$50-150/rooftop/mo, bundled into $1,599 subscription

Tier 3 — Manual CSV upload (10% — small independents)
- Office manager drops the daily Declined Services report into the app
- Takes 30 seconds/day
- Already live in-product ("Nightly Import" button)

Onboarding pitch to dealer:
"We handle the DMS data pull as part of onboarding. Sign the data consent form, we work with DealerVault to establish a nightly feed to our platform, and you never touch it again. Setup takes about a week and is included in your onboarding fee."

TECH STACK
- Backend: FastAPI + MongoDB
- Frontend: React + Tailwind + shadcn/ui
- SMS: Twilio (mocked until keys added to backend/.env)
- Auth: JWT email/password
- Hosting: Emergent platform
- Scheduler: /app/.emergent/crons.yml

ROADMAP
- Q2 2026: Live Twilio + two-way SMS · Advisor logins with roles · SFTP nightly auto-pull
- Q3 2026: Stripe ACH billing · Multi-rooftop / white-label · TCPA compliance suite
- Q4 2026: Certified DMS integration (CDK, Reynolds, Tekion) · Weekly advisor emails

PRICING SUMMARY
- One-time onboarding: $895
- Monthly subscription: $1,599 / rooftop
- Contract: 12 months, auto-renew
- Guarantee: 30-day onboarding refund if first SMS doesn't fire

© 2026 Decline Drive™ by Ayers Enterprises. All rights reserved.
`;

export default function SpecsPage() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SPECS);
      setCopied(true);
      toast.success("Specs copied to clipboard");
      setTimeout(() => setCopied(false), 3000);
    } catch (_) {
      toast.error("Copy failed — long-press the text below to select");
    }
  };
  const mailto = `mailto:jayers.me@gmail.com?subject=${encodeURIComponent(
    "Decline Drive — Complete Product Specifications"
  )}&body=${encodeURIComponent(SPECS)}`;

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Product Specifications" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Reference</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Product Specs</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Everything Decline Drive does, in one document. Tap <b>Copy</b> to paste it anywhere, or tap{" "}
            <b>Email to me</b> to open your mail app pre-filled.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={copy} data-testid="copy-specs-btn">
            <Copy className="w-4 h-4 mr-2" strokeWidth={1.75} />
            {copied ? "Copied!" : "Copy specs"}
          </Button>
          <a href={mailto} data-testid="email-specs-link">
            <Button variant="outline">
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Email to me
            </Button>
          </a>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-specs-btn">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Print
          </Button>
        </div>
      </div>

      <pre
        className="whitespace-pre-wrap font-mono text-xs leading-relaxed bg-card border border-border rounded-lg p-6 max-w-4xl text-foreground select-all"
        data-testid="specs-content"
      >
        {SPECS}
      </pre>
    </DashboardShell>
  );
}
